import React, { useState, useEffect, useRef } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  MessageCircle,
  Send,
  Inbox,
  Mail,
  Users,
  Bell,
  BellOff,
  Loader2,
  Archive
} from 'lucide-react';
import MessageList from './MessageList';
import ComposeMessage from './ComposeMessage';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import { connect, sendMessage } from '../utils/websocket';
import api from '../utils/api';

const MessagingPanel = () => {
  const [activeTab, setActiveTab] = useState('inbox');
  const [messages, setMessages] = useState([]);
  const [sentMessages, setSentMessages] = useState([]);
  const [archivedMessages, setArchivedMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [tabLoading, setTabLoading] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const { toast } = useToast();
  const abortControllerRef = useRef(null);

  const fetchWithRetry = async (requestFn, maxRetries = 3) => {
    let lastError;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await requestFn();
      } catch (error) {
        lastError = error;
        
        // Don't retry on cancellation
        if (error.name === 'CanceledError') {
          throw error;
        }
        
        // Don't retry on last attempt
        if (attempt === maxRetries) {
          break;
        }
        
        // Wait before retry (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
      }
    }
    
    throw lastError;
  };

  const fetchMessages = async () => {
    console.log('MessagingPanel: Fetching messages...');
    // Cancel previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    setTabLoading(true);
    
    try {
      const [inboxResponse, sentResponse, archivedResponse, unreadResponse] = await Promise.all([
        fetchWithRetry(async () => {
          return await api.get('/messages', { signal: abortControllerRef.current.signal });
        }),
        fetchWithRetry(async () => {
          return await api.get('/messages/sent', { signal: abortControllerRef.current.signal });
        }),
        fetchWithRetry(async () => {
          return await api.get('/messages/archived', { signal: abortControllerRef.current.signal });
        }),
        fetchWithRetry(async () => {
          return await api.get('/messages/unread-count', { signal: abortControllerRef.current.signal });
        })
      ]);
      
      console.log('MessagingPanel: Messages fetched successfully', {
        inbox: inboxResponse.data.length,
        sent: sentResponse.data.length,
        archived: archivedResponse.data.length,
        unread: unreadResponse.data.unread_count
      });

      setMessages(inboxResponse.data);
      setSentMessages(sentResponse.data);
      setArchivedMessages(archivedResponse.data);
      setUnreadCount(unreadResponse.data.unread_count);
    } catch (error) {
      if (error.name !== 'CanceledError') {
        console.error('Error fetching messages:', error);
        toast({
          title: "Error",
          description: "Failed to fetch messages. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setTabLoading(false);
      abortControllerRef.current = null;
    }
  };

  const fetchUsers = async () => {
    console.log('MessagingPanel: Fetching users...');
    try {
      const response = await api.get('/users');
      const activeUsers = response.data.filter(user => user.status === 'active');
      console.log('MessagingPanel: Users fetched successfully', { total: response.data.length, active: activeUsers.length });
      setUsers(activeUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('exled_user'));
    
    if (!user || !user.id) {
      console.log('MessagingPanel: No user found, skipping initialization');
      return;
    }

    const handleNewMessage = (data) => {
      const message = JSON.parse(data);
      // Check if the message is for the current user
      if (message.recipient_id === user.id) {
        setMessages((prevMessages) => [message, ...prevMessages]);
        setUnreadCount((prevCount) => prevCount + 1);
      }
    };

    connect(user.id, handleNewMessage);
    fetchMessages();
    fetchUsers();
    
    // Set up polling for new messages every 30 seconds
    const interval = setInterval(fetchMessages, 30000);
    return () => {
      clearInterval(interval);
      // Cancel any pending requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const handleSendMessage = async (messageData) => {
    console.log('📤 Sending message:', messageData);
    setSendLoading(true);

    try {
      // Use the api instance instead of axios directly
      const response = await api.post('/messages', messageData);
      console.log('✅ Message sent successfully:', response.data);

      toast({
        title: "Success",
        description: "Message sent successfully",
      });

      setShowCompose(false);
      fetchMessages(); // Refresh messages
    } catch (error) {
      console.error('Error sending message:', error);

      // Handle specific authentication errors without causing white screen
      if (error.response?.status === 401) {
        toast({
          title: "Authentication Error",
          description: "Your session has expired. Please refresh the page.",
          variant: "destructive",
        });
        return; // Don't close modal, let user try again
      }

      // Handle network errors
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        toast({
          title: "Network Error",
          description: "Connection timeout. Please check your internet connection and try again.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Error",
        description: error.response?.data?.detail || error.message || "Failed to send message",
        variant: "destructive",
      });
    } finally {
      setSendLoading(false);
    }
  };

  const handleMarkRead = async (messageId) => {
    try {
      await api.put(`/messages/${messageId}/read`);
      fetchMessages(); // Refresh to update read status
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    console.log('🗑️ MessagingPanel: handleDeleteMessage called for messageId:', messageId);

    try {
      console.log('📡 Making DELETE API call to:', `/messages/${messageId}`);
      const response = await api.delete(`/messages/${messageId}`);
      console.log('📡 API response:', response);

      if (response.status === 200) {
        console.log('✅ Message deleted successfully, removing from state');

        // Check if the deleted message was unread to update the count
        const deletedMessage = [...messages, ...sentMessages, ...archivedMessages].find(msg => msg.id === messageId);
        const wasUnread = deletedMessage && !deletedMessage.is_read && deletedMessage.recipient_id === JSON.parse(localStorage.getItem('exled_user'))?.id;

        // Remove the message from the appropriate list
        setMessages(prev => prev.filter(msg => msg.id !== messageId));
        setSentMessages(prev => prev.filter(msg => msg.id !== messageId));
        setArchivedMessages(prev => prev.filter(msg => msg.id !== messageId));

        // Update unread count if the deleted message was unread
        if (wasUnread) {
          setUnreadCount(prev => Math.max(0, prev - 1));
          console.log('📊 Updated unread count (deleted message was unread)');
        }

        toast({
          title: "Message Deleted",
          description: "Message has been deleted successfully",
        });
      } else {
        throw new Error('Failed to delete message');
      }
    } catch (error) {
      console.error('❌ Error deleting message:', error);
      toast({
        title: "Error",
        description: "Failed to delete message. Please try again.",
        variant: "destructive",
      });
      // Refresh messages to ensure UI is consistent
      fetchMessages();
    }
  };

  const handleArchiveMessage = async (messageId) => {
    console.log('📁 MessagingPanel: handleArchiveMessage called for messageId:', messageId);

    try {
      console.log('📡 Making Archive API call to:', `/messages/${messageId}/archive`);
      const response = await api.put(`/messages/${messageId}/archive`);
      console.log('📡 API response:', response);

      if (response.status === 200) {
        console.log('✅ Message archived successfully, removing from state');

        // Check if the archived message was unread to update the count
        const archivedMessage = [...messages, ...sentMessages].find(msg => msg.id === messageId);
        const wasUnread = archivedMessage && !archivedMessage.is_read && archivedMessage.recipient_id === JSON.parse(localStorage.getItem('exled_user'))?.id;

        // Remove the message from the appropriate list
        setMessages(prev => prev.filter(msg => msg.id !== messageId));
        setSentMessages(prev => prev.filter(msg => msg.id !== messageId));

        // Update unread count if the archived message was unread
        if (wasUnread) {
          setUnreadCount(prev => Math.max(0, prev - 1));
          console.log('📊 Updated unread count (archived message was unread)');
        }

        toast({
          title: "Message Archived",
          description: "Message has been archived successfully",
        });
      } else {
        throw new Error('Failed to archive message');
      }
    } catch (error) {
      console.error('❌ Error archiving message:', error);
      toast({
        title: "Error",
        description: "Failed to archive message. Please try again.",
        variant: "destructive",
      });
      // Refresh messages to ensure UI is consistent
      fetchMessages();
    }
  };

  const handleMessageClick = (message) => {
    if (!message.product_id) return;
    
    // Switch to product development tab
    setActiveTab('product_development');
    
    // Dispatch custom event to open product and navigate to specific note
    const event = new CustomEvent('openProductForNote', {
      detail: {
        productId: message.product_id,
        noteId: message.metadata?.note_id,
        message: message
      }
    });
    
    // Dispatch the event to be handled by the Dashboard component
    window.dispatchEvent(event);
    
    toast({
      title: "Opening Product",
      description: "Navigating to product details...",
    });
  };

  // Check if user is authenticated
  const user = JSON.parse(localStorage.getItem('exled_user'));
  if (!user || !user.id) {
    return (
      <div className="space-y-6">
        <div className="text-center py-16">
          <MessageCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Messages</h3>
          <p className="text-slate-600">Please log in to access your messages.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Messaging Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <MessageCircle className="w-5 h-5 text-teal-600" />
            <h3 className="text-lg font-semibold text-slate-900">Messages</h3>
          </div>
          {unreadCount > 0 && (
            <Badge className="bg-red-500 text-white">
              {unreadCount} unread
            </Badge>
          )}
        </div>
        <Button
          onClick={() => setShowCompose(true)}
          className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700"
        >
          <Send className="w-4 h-4 mr-2" />
          Compose
        </Button>
      </div>

      {/* Messaging Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-3 bg-slate-100 rounded-lg p-1">
          <TabsTrigger value="inbox" className="flex items-center space-x-2">
            <Inbox className="w-4 h-4" />
            <span>Inbox</span>
            {unreadCount > 0 && (
              <Badge variant="destructive" className="h-5 px-1.5 text-xs">
                {unreadCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="sent" className="flex items-center space-x-2">
            <Mail className="w-4 h-4" />
            <span>Sent</span>
          </TabsTrigger>
          <TabsTrigger value="archived" className="flex items-center space-x-2">
            <Archive className="w-4 h-4" />
            <span>Archives</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="inbox" className="space-y-4">
          <MessageList
            messages={messages}
            loading={tabLoading}
            onMarkRead={handleMarkRead}
            onDelete={handleDeleteMessage}
            onArchive={handleArchiveMessage}
            users={users}
            type="inbox"
            onMessageClick={handleMessageClick}
          />
        </TabsContent>

        <TabsContent value="sent" className="space-y-4">
          <MessageList
            messages={sentMessages}
            loading={tabLoading}
            onMarkRead={handleMarkRead}
            onDelete={handleDeleteMessage}
            onArchive={handleArchiveMessage}
            users={users}
            type="sent"
            onMessageClick={handleMessageClick}
          />
        </TabsContent>

        <TabsContent value="archived" className="space-y-4">
          <MessageList
            messages={archivedMessages}
            loading={tabLoading}
            onMarkRead={handleMarkRead}
            onDelete={handleDeleteMessage}
            onArchive={handleArchiveMessage}
            users={users}
            type="archived"
            onMessageClick={handleMessageClick}
          />
        </TabsContent>
      </Tabs>

      {/* Compose Message Modal */}
      <ComposeMessage
        isOpen={showCompose}
        users={users}
        onSend={handleSendMessage}
        onClose={() => setShowCompose(false)}
      />
    </div>
  );
};

export default MessagingPanel;